/**
 * CardRenderer — generic card. `card_type` (activity, place, hotel,
 * restaurant, weather, flight, link, experience, tour) is currently a
 * visual hint only — we render the same layout for every kind. The hint
 * survives the wire so future per-type tweaks (e.g. weather-specific
 * widget) can branch on it without a new content kind.
 *
 * Layout (380px wide, embed iframe):
 *   ┌──────────────────────────────┐
 *   │ [image, 16:9]                │
 *   │ Title                        │
 *   │ Description (2 lines clamp)  │
 *   │ ★ 4.8 · $99 · [tag] [tag]    │
 *   │ [Action 1]  [Action 2]       │
 *   └──────────────────────────────┘
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CardActionRef, CardContentItem } from '@wg/types';
import type { Renderer, RenderCtx } from '../registry';
import { dispatchCardAction } from '../actions';
import logger from '../../utils/logger';

/**
 * Classify a card's "bookability" so the UI can warn the visitor up-front
 * that this is a real bookable thing, not just an info card. Stronger tone
 * (`reserve`) for cards that prefill checkout or book directly; weaker
 * (`plan`) for cards that only add to the itinerary.
 */
function bookableKind(actions?: CardActionRef[]): 'reserve' | 'plan' | null {
  if (!actions || actions.length === 0) return null;
  const kinds = new Set(actions.map(a => a.kind));
  if (kinds.has('prefill_checkout') || kinds.has('book')) return 'reserve';
  if (kinds.has('add_to_itinerary')) return 'plan';
  return null;
}

function fireAction(action: CardActionRef, ctx: RenderCtx): void {
  if (!ctx.actionDeps) {
    // Action dispatcher not wired by host — legacy fallback for open_url so
    // tools that emit cards before ChatView attaches actionDeps still work.
    if (action.kind === 'open_url') {
      const url = (action.data?.url as string | undefined) || undefined;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    logger.warn('[CardRenderer] action fired without actionDeps', {
      kind: action.kind,
    });
    return;
  }
  dispatchCardAction(action, ctx.actionDeps);
}

function formatPrice(p: CardContentItem['price']): string | null {
  if (!p) return null;
  if (p.display) return p.display;
  if (typeof p.amount === 'number' && p.currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: p.currency,
        maximumFractionDigits: 0,
      }).format(p.amount);
    } catch {
      return `${p.amount} ${p.currency}`;
    }
  }
  return null;
}

const CardRenderer: Renderer = ({ item, ctx }) => {
  const { t } = useTranslation();
  const c = item as CardContentItem;
  const price = formatPrice(c.price);
  const ratingDisplay =
    c.rating && typeof c.rating.average === 'number'
      ? `★ ${c.rating.average.toFixed(1)}${c.rating.count ? ` (${c.rating.count})` : ''}`
      : null;
  const bookable = bookableKind(c.actions);

  return (
    <div className="wg-chat__card" data-card-type={c.card_type}>
      {c.image_url && (
        <div className="wg-chat__card-imagebox">
          <img
            className="wg-chat__card-image"
            src={c.image_url}
            alt={c.title}
            loading="lazy"
            decoding="async"
          />
          {bookable && (
            <span
              className={`wg-chat__card-badge wg-chat__card-badge--${bookable}`}
            >
              <BookableIcon />
              {bookable === 'reserve'
                ? t('card.bookable_reserve')
                : t('card.bookable_plan')}
            </span>
          )}
        </div>
      )}
      {!c.image_url && bookable && (
        <span
          className={`wg-chat__card-badge wg-chat__card-badge--${bookable} wg-chat__card-badge--inline`}
        >
          <BookableIcon />
          {bookable === 'reserve'
            ? t('card.bookable_reserve')
            : t('card.bookable_plan')}
        </span>
      )}
      <div className="wg-chat__card-body">
        <h3 className="wg-chat__card-title">{c.title}</h3>
        {c.description && <p className="wg-chat__card-desc">{c.description}</p>}
        {(price || ratingDisplay || (c.tags && c.tags.length > 0)) && (
          <div className="wg-chat__card-meta">
            {ratingDisplay && (
              <span className="wg-chat__card-rating">{ratingDisplay}</span>
            )}
            {price && <span className="wg-chat__card-price">{price}</span>}
            {(c.tags || []).slice(0, 3).map(t => (
              <span key={t} className="wg-chat__card-tag">
                {t}
              </span>
            ))}
          </div>
        )}
        {c.actions && c.actions.length > 0 && (
          <div className="wg-chat__card-actions">
            {c.actions.map((a, i) => (
              <button
                key={`${a.kind}-${i}`}
                type="button"
                className={`wg-chat__card-action wg-chat__card-action--${a.weight ?? 'secondary'}`}
                onClick={() => fireAction(a, ctx)}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
        {c.attribution?.text && (
          <p className="wg-chat__card-attr">
            {c.attribution.url ? (
              <a
                href={c.attribution.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {c.attribution.text}
              </a>
            ) : (
              c.attribution.text
            )}
          </p>
        )}
      </div>
    </div>
  );
};

function BookableIcon() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <path d="M9 16l2 2 4-4" />
    </svg>
  );
}

export default CardRenderer;
